import { pipe, Option, Array as A } from 'effect';
import moment from 'moment';

import type { BankFeedSetFilterModel } from '@/domain/bankFeed/schemas';

// AG Grid `gridApi.getFilterModel()` returns `{ [colId]: any }` upstream. We narrow
// defensively via the `filterType` discriminant. BE ships `agTextColumnFilter`
// (string), `agNumberColumnFilter` (amount), the custom set-filter emitted by
// `BankFeedSetColumnFilter` for bank/select/transaction_status, and the shared custom
// date filter (`AgDateFilterBE`, `filterType: 'date'`) for date columns.
//
// Operator labels (text/number/date) and the company date format are passed in by the
// caller via `labels` rather than hardcoded — keeps this module pure and lets
// `ActiveFilterChips` share the same i18n dictionary AG Grid's own header-menu popup uses
// (`src/components/AgGrid/helpers/translations.js`), so chip text matches what the user
// just clicked. Dates render in the company format (see `formatCompanyDate` below).

type SimpleTextFilter = {
  filterType: 'text';
  type?: string;
  filter?: string | null;
};

type SimpleNumberFilter = {
  filterType: 'number';
  type?: string;
  filter?: number | null;
  filterTo?: number | null;
};

type SimpleDateFilter = {
  filterType: 'date';
  type?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
};

type CombinedFilter = {
  // Both AG Grid's built-in filters and the custom date filter (`AgDateFilterBE`, which
  // uppercases via `useSubmitAgModel`) persist an uppercase operator in the filter model.
  operator: 'AND' | 'OR';
  conditions: ReadonlyArray<unknown>;
};

export type OperatorLabels = {
  // AG Grid filter `type` (e.g. 'contains', 'greaterThan') → display string.
  // Same dictionary feeds AG Grid's own header-menu popup via `getLocaleText`.
  operators: Partial<Record<string, string>>;
  and: string;
  or: string;
  // Company date format (e.g. 'DD/MM/YYYY') — same source the date cells use.
  dateFormat: string;
};

const operatorOf = (entry: { type?: string }, labels: OperatorLabels): string =>
  pipe(
    Option.fromNullable(entry.type),
    Option.flatMapNullable((t) => labels.operators[t]),
    Option.getOrElse(() => entry.type ?? ''),
  );

const formatText = (entry: SimpleTextFilter, labels: OperatorLabels): string => {
  const op = operatorOf(entry, labels);
  return pipe(
    Option.fromNullable(entry.filter),
    Option.match({
      onNone: () => op,
      onSome: (val) => (op !== '' ? `${op} "${val}"` : `"${val}"`),
    }),
  );
};

const formatNumber = (entry: SimpleNumberFilter, labels: OperatorLabels): string => {
  if (entry.type === 'inRange') {
    const from = pipe(
      Option.fromNullable(entry.filter),
      Option.map(String),
      Option.getOrElse(() => ''),
    );
    const to = pipe(
      Option.fromNullable(entry.filterTo),
      Option.map(String),
      Option.getOrElse(() => ''),
    );
    return `${from}–${to}`;
  }
  const op = operatorOf(entry, labels);
  return pipe(
    Option.fromNullable(entry.filter),
    Option.match({
      onNone: () => op,
      onSome: (v) => (op !== '' ? `${op} ${v}` : String(v)),
    }),
  );
};

const formatSet = (entry: BankFeedSetFilterModel, labels: OperatorLabels): string => {
  const op = operatorOf(entry, labels);
  const joined = entry.values.map((v) => v.label).join(', ');
  if (joined.length === 0) return op;
  return op !== '' ? `${op} "${joined}"` : `"${joined}"`;
};

// The custom date filter sends `dateFrom`/`dateTo` as 'YYYY-MM-DD HH:mm:ss'. Chip rendering
// uses the company date format throughout:
//   • By year  → '2026'      (period only, no operator label)
//   • By month → 'Jun 2026'  (period only, no operator label)
//   • In range → 'from–to'   (company-formatted, no operator label — like the number range)
//   • before/after/etc → '{operator} {company-formatted date}'
const formatCompanyDate = (iso: string | null | undefined, dateFormat: string): string =>
  iso ? moment(iso).format(dateFormat) : '';

const formatDate = (entry: SimpleDateFilter, labels: OperatorLabels): string => {
  const { type, dateFrom, dateTo } = entry;
  if (type === 'withInYear') return dateFrom ? String(moment(dateFrom).year()) : '';
  if (type === 'withInMonth') return dateFrom ? moment(dateFrom).format('MMM YYYY') : '';
  if (type === 'inRange') {
    return `${formatCompanyDate(dateFrom, labels.dateFormat)}–${formatCompanyDate(dateTo, labels.dateFormat)}`;
  }
  const op = operatorOf(entry, labels);
  const from = formatCompanyDate(dateFrom, labels.dateFormat);
  if (from === '') return op;
  return op !== '' ? `${op} ${from}` : from;
};

const isCombined = (entry: unknown): entry is CombinedFilter =>
  typeof entry === 'object' &&
  entry !== null &&
  'conditions' in entry &&
  Array.isArray((entry as { conditions: unknown }).conditions);

export const formatFilterValue = (entry: unknown, labels: OperatorLabels): string => {
  if (entry === null || typeof entry !== 'object') return '';

  if (isCombined(entry)) {
    // AG Grid combined-filter conditions inherit `filterType` from the parent —
    // propagate when missing so the recursive call dispatches correctly.
    const parentFilterType = (entry as { filterType?: string }).filterType;
    const joiner = entry.operator === 'OR' ? ` ${labels.or} ` : ` ${labels.and} `;
    return pipe(
      entry.conditions,
      A.map((cond) => {
        if (cond == null || typeof cond !== 'object') return '';
        if ('filterType' in cond) return formatFilterValue(cond, labels);
        return formatFilterValue({ ...cond, filterType: parentFilterType }, labels);
      }),
      A.filter((s) => s.length > 0),
    ).join(joiner);
  }

  const filterType = (entry as { filterType?: string }).filterType;
  switch (filterType) {
    case 'text':
      return formatText(entry as SimpleTextFilter, labels);
    case 'number':
      return formatNumber(entry as SimpleNumberFilter, labels);
    case 'date':
      return formatDate(entry as SimpleDateFilter, labels);
    case 'set':
      return formatSet(entry as BankFeedSetFilterModel, labels);
    default:
      return '';
  }
};
